
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MediaItem, AppSettings, MediaType, DriveAuthSession } from './types';
import type { PlanTier } from './types/plan';
import type { PlanResponse } from './services/planService';
import { initGapiClient, signInToGoogle, signOut, listFolderMediaFiles } from './services/driveService';
import { generateSearchTags } from './services/geminiService';
import { mediaCache } from './services/mediaCache';
import { imageProcessingService } from './services/imageProcessingService';
import { storageService, STORES } from './services/storageService';
import { metadataService } from './services/metadataService';
import { searchCacheService } from './services/searchCacheService';
import { subscriptionService } from './services/subscriptionService';
import { usageService } from './services/usageService';
import { planService } from './services/planService';
import { SubscriptionModal } from './components/SubscriptionModal';
import { UpgradePrompt } from './components/UpgradePrompt';
import { LibraryUsageBar } from './components/LibraryUsageBar';
import { ApiKeyModal } from './components/ApiKeyModal';
import { CheckoutSuccess } from './components/CheckoutSuccess';
import { CheckoutCancel } from './components/CheckoutCancel';
import { SubscriptionTier } from './constants/subscriptionLimits';
import PlayerRedesign, { EtherealPlayer } from './components/PlayerRedesign';
import { Controls } from './components/Controls';
import { SettingsModal } from './components/SettingsModal';
import { SearchModal } from './components/SearchModal';
import { PasteLinksModal } from './components/PasteLinksModal';
import { LoadingScreen } from './components/LoadingScreen';
import HomepageDesign3 from './components/HomepageDesign3';
import { WelcomeScreen } from './components/WelcomeScreen';
import { ErrorButton } from './components/ErrorButton';
import { Shuffle, LogOut, Plus, ImagePlus, Search, XCircle, Crown, Key } from 'lucide-react';
import { getMediaType } from './utils/mediaType';
import { libraryService, type UpsertResponse } from './services/libraryService';
import { summarizeImportOutcome } from './services/importSummary';
import { getDriveFileFolderImportEmptyMessage } from './services/driveFileLimitations';
import { openDrivePicker, parseDriveLinksAndIds, resolveBulkImportIntent } from './services/pickerService';
import { addSentryBreadcrumb, captureSentryException, captureSentryMessage, clearSentryAuthContext, setSentryAuthContext } from './sentry';
import { Toaster, toast } from 'sonner';

const App: React.FC = () => {
  // Auth & Data State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [authSession, setAuthSession] = useState<DriveAuthSession | null>(null);
  const [libraryItems, setLibraryItems] = useState<MediaItem[]>([]);
  const [localItems, setLocalItems] = useState<MediaItem[]>([]);
  const [syncToken, setSyncToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>('free');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');
  const [targetUpgradeTier, setTargetUpgradeTier] = useState<SubscriptionTier>('pro');
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null);
  const [showCheckoutCancel, setShowCheckoutCancel] = useState(false);

  // Plan/Usage State (server-backed)
  const [planData, setPlanData] = useState<PlanResponse | null>(null);

  // Search State
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<MediaItem[]>([]);
  const [searchTags, setSearchTags] = useState<string[]>([]);

  // Playback State
  const [shuffledQueue, setShuffledQueue] = useState<MediaItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [settings, setSettings] = useState<AppSettings>({
    filter: MediaType.ALL,
    interval: 5,
    isPlaying: false,
    showInfo: true
  });

  // UI State
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showMigrationBanner, setShowMigrationBanner] = useState(false);
  const [showPasteLinksModal, setShowPasteLinksModal] = useState(false);

  // User Interaction State (for hiding controls)
  const [controlsVisible, setControlsVisible] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const slideshowTimerRef = useRef<number | null>(null);
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);
  const shuffledQueueRef = useRef<MediaItem[]>([]);
  const HEIC_PREFETCH_COUNT = 4;
  const showDebugControls = Boolean(import.meta.env.DEV);

  // ── Derived state ──
  const aiEnabled = planData?.features?.aiSearch ?? false;
  const collectionsEnabled = planData?.features?.collections ?? false;
  const driveItemsUsed = Math.max(planData?.usage?.driveItemsUsed ?? 0, libraryItems.length);
  const driveItemsLimit = planData?.usage?.driveItemsLimit ?? 2000;
  const currentTier: PlanTier = planData?.tier ?? 'free';

  const isHeicItem = useCallback((item: MediaItem) => (
    item.mimeType === 'image/heic' ||
    item.mimeType === 'image/heif' ||
    item.mimeType === 'image/heic-sequence' ||
    item.mimeType === 'image/heif-sequence' ||
    item.name.toLowerCase().endsWith('.heic') ||
    item.name.toLowerCase().endsWith('.heif')
  ), []);

  // ── Fetch plan from server ──
  const refreshPlan = useCallback(async (session: DriveAuthSession) => {
    try {
      const data = await planService.getPlan(session);
      setPlanData(data);
      setSubscriptionTier(data.tier);
    } catch {
      // Default to free on error
    }
  }, []);

  // ── Initialization ──
  const hydrateFromServerLibrary = useCallback(async (session: DriveAuthSession, legacyUserId: string | null) => {
    const authorized = await libraryService.getLibrary(session);
    const driveItems: MediaItem[] = authorized.map((item) => ({
      id: item.fileId,
      name: item.name,
      mimeType: item.mimeType,
      source: 'drive',
      driveFile: {
        id: item.fileId,
        name: item.name,
        mimeType: item.mimeType,
        thumbnailLink: item.thumbnailLink,
      },
    }));

    setLibraryItems(driveItems);
    reshuffle(driveItems, localItems, settings.filter);

    if (legacyUserId) {
      await storageService.saveMediaMetadata([
        ...driveItems.map((item) => ({
          id: item.id,
          userId: legacyUserId,
          driveFileId: item.driveFile?.id,
          name: item.name,
          mimeType: item.mimeType,
          source: 'drive' as const,
          indexedAt: new Date().toISOString(),
          thumbnailUrl: item.driveFile?.thumbnailLink,
        })),
      ]);
    }

    setShowMigrationBanner(driveItems.length === 0);
  }, [localItems, settings.filter]);

  useEffect(() => {
    const initApp = async () => {
      try {
        await initGapiClient();
        const storedSession = await storageService.getAuthSession();

        if (storedSession?.accessToken) {
          const session: DriveAuthSession = {
            accessToken: storedSession.accessToken,
            idToken: storedSession.idToken,
            expiresAt: Date.now() + 30 * 60 * 1000,
            userSub: storedSession.userSub,
            email: storedSession.email,
          };
          setAuthSession(session);
          setAccessToken(storedSession.accessToken);
          setIsAuthenticated(true);

          if (window.gapi?.client) {
            window.gapi.client.setToken({ access_token: storedSession.accessToken });
          }

          // Use Google's stable `sub` for consistent cross-session identity
          const stableUserId = storedSession.userSub || `user-${btoa(storedSession.accessToken).substring(0, 16)}`;
          setUserId(stableUserId);
          if (storedSession.userSub) setSentryAuthContext(storedSession.userSub);

          // Fetch plan from server
          await refreshPlan(session);

          const localMetadata = await storageService.getMediaMetadata(stableUserId);
          const localOnly = localMetadata.filter((m) => m.source === 'local');
          const restoredLocal: MediaItem[] = [];
          for (const meta of localOnly) {
            if (!meta.localFileHash) continue;
            try {
              const file = await storageService.getLocalFile(meta.localFileHash);
              if (file) restoredLocal.push({ id: meta.id, name: meta.name, mimeType: meta.mimeType, source: 'local', file });
            } catch (restoreError: any) {
              captureSentryMessage('indexeddb_restore_failed', {
                mediaId: meta.id,
                source: meta.source,
                error: restoreError?.message || 'unknown',
              });
            }
          }
          setLocalItems(restoredLocal);

          await hydrateFromServerLibrary(session, stableUserId);
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to initialize.');
      }
    };

    initApp();
    return () => stopSlideShow();
  }, [hydrateFromServerLibrary, refreshPlan]);

  // ── Persistence Savers ──
  useEffect(() => {
    if (isAuthenticated && (libraryItems.length > 0 || localItems.length > 0)) {
      storageService.saveMediaItems([...libraryItems, ...localItems]);
    }
  }, [libraryItems, localItems, isAuthenticated]);

  useEffect(() => {
    shuffledQueueRef.current = shuffledQueue;
  }, [shuffledQueue]);

  // ── Preloading Logic ──
  useEffect(() => {
    if (shuffledQueue.length === 0) return;
    const itemsToPreload = [];
    const keepIds = new Set<string>();
    const current = shuffledQueue[currentIndex];
    if (current) { itemsToPreload.push(current); keepIds.add(current.id); }
    for (let i = 1; i <= 4; i++) {
      const next = shuffledQueue[(currentIndex + i) % shuffledQueue.length];
      if (next) { itemsToPreload.push(next); keepIds.add(next.id); }
    }
    const prev = shuffledQueue[(currentIndex - 1 + shuffledQueue.length) % shuffledQueue.length];
    if (prev) keepIds.add(prev.id);

    itemsToPreload.forEach(item => {
      if (!mediaCache.has(item.id)) mediaCache.preload(item, accessToken);
    });

    for (let i = 1; i <= HEIC_PREFETCH_COUNT; i++) {
      const next = shuffledQueue[(currentIndex + i) % shuffledQueue.length];
      if (!next || !isHeicItem(next)) continue;
      if (next.source === 'drive' && !accessToken) continue;
      mediaCache.loadProcessedImages(next, accessToken)
        .then(images => imageProcessingService.revokeUrls(images))
        .catch(() => { });
    }

    mediaCache.prune(Array.from(keepIds));
  }, [currentIndex, shuffledQueue, accessToken, isHeicItem]);

  useEffect(() => { setIsMediaLoaded(false); }, [currentIndex]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (libraryItems.length === 0 && localItems.length === 0 && searchResults.length === 0) return;
      if (isSearchMode) {
        const d = searchResults.filter(i => i.source === 'drive');
        const l = searchResults.filter(i => i.source === 'local');
        reshuffle(d, l, settings.filter);
      } else {
        reshuffle(libraryItems, localItems, settings.filter);
      }
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [settings.filter]);

  // ── Auth Handlers ──
  const handleLogin = async () => {
    setError(null);
    try {
      const session = await signInToGoogle();
      setAuthSession(session);
      setAccessToken(session.accessToken);
      setIsAuthenticated(true);
      setShowWelcomeScreen(false);

      if (window.gapi?.client) {
        window.gapi.client.setToken({ access_token: session.accessToken });
      }

      await storageService.saveAuth({
        accessToken: session.accessToken,
        idToken: session.idToken,
        userSub: session.userSub,
        email: session.email,
      });

      // Use Google's stable `sub` for consistent cross-session identity
      const stableUserId = session.userSub || `user-${btoa(session.accessToken).substring(0, 16)}`;
      setUserId(stableUserId);
      if (session.userSub) setSentryAuthContext(session.userSub);

      // Fetch plan from server (also updates subscriptionTier)
      await refreshPlan(session);

      await hydrateFromServerLibrary(session, stableUserId);

      // Navigate to PlayerRedesign (/2) after successful sign-in
      window.location.href = '/2';
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    }
  };

  const handleGetStarted = () => {
    setShowWelcomeScreen(true);
    setError(null);
  };

  const handleLogout = async () => {
    setError(null);
    try { await signOut(); } catch { }
    try { await storageService.clearAuth(); } catch { }
    setIsAuthenticated(false);
    setAccessToken(null);
    setAuthSession(null);
    setLibraryItems([]);
    setLocalItems([]);
    setShuffledQueue([]);
    setIsSearchMode(false);
    setShowMigrationBanner(false);
    setPlanData(null);
    clearSentryAuthContext();
    stopSlideShow();
    // Navigate back to home page
    window.location.href = '/';
  };

  // ── Data Loading ──
  const importFromPicker = async (presetFileIds?: string[]) => {
    if (!authSession) return;

    try {
      addSentryBreadcrumb('picker_open');
      const selection = await openDrivePicker({
        accessToken: authSession.accessToken,
        presetFileIds,
      });

      const selectedFiles = [...selection.files];
      addSentryBreadcrumb('picker_selected', { count: selectedFiles.length, folderCount: selection.folderIds.length });

      if (selection.folderIds.length > 0) {
        toast.loading('Importing all photos/videos from selected folder(s)...', { id: 'folder-import-status' });
        const failedFolderIds: string[] = [];
        for (const folderId of selection.folderIds) {
          try {
            const folderFiles = await listFolderMediaFiles(authSession.accessToken, folderId);
            selectedFiles.push(...folderFiles.map((file) => ({
              fileId: file.id, name: file.name, mimeType: file.mimeType, thumbnailLink: file.thumbnailLink,
            })));
          } catch (folderErr) {
            console.warn('Folder import failed for one folder:', folderErr);
            failedFolderIds.push(folderId);
          }
        }
        if (failedFolderIds.length > 0) {
          throw new Error(`Failed to fully scan ${failedFolderIds.length} selected folder(s). No partial import was applied. Please retry.`);
        }
      }

      const dedup = new Map(selectedFiles.map((item) => [item.fileId, item]));
      const finalSelection = Array.from(dedup.values());
      if (finalSelection.length === 0) {
        toast.info(getDriveFileFolderImportEmptyMessage(selection.folderIds.length), { id: 'folder-import-status', duration: 7000 });
        return;
      }

      // Pre-flight capacity check
      const remaining = driveItemsLimit - driveItemsUsed;
      if (remaining <= 0) {
        toast.error(`Library is full (${driveItemsUsed}/${driveItemsLimit}). Upgrade your plan to add more items.`, {
          id: 'folder-import-status', duration: 6000,
        });
        setUpgradeReason('Your Drive library is full. Upgrade to add more items.');
        setTargetUpgradeTier('pro');
        setShowUpgradePrompt(true);
        return;
      }

      if (finalSelection.length > remaining) {
        toast.info(
          `You selected ${finalSelection.length} items but only ${remaining} slots remain. Importing first ${remaining}.`,
          { id: 'folder-import-status', duration: 5000 }
        );
      }

      addSentryBreadcrumb('library_upsert_start', { count: finalSelection.length });
      const upsertResult: UpsertResponse = await libraryService.upsertLibrary(authSession, finalSelection.map((item) => ({
        fileId: item.fileId, name: item.name, mimeType: item.mimeType, thumbnailLink: item.thumbnailLink,
      })));

      addSentryBreadcrumb('library_upsert_success', { added: upsertResult.added, skipped: upsertResult.skipped });
      const summary = summarizeImportOutcome({
        selectedCount: finalSelection.length,
        added: upsertResult.added,
        skipped: upsertResult.skipped,
        limitExceeded: upsertResult.limitExceeded,
      });

      const items: MediaItem[] = upsertResult.items.map((item) => ({
        id: item.fileId,
        name: item.name,
        mimeType: item.mimeType,
        source: 'drive',
        driveFile: {
          id: item.fileId, name: item.name, mimeType: item.mimeType, thumbnailLink: item.thumbnailLink,
        },
      }));

      setError(null);

      if (upsertResult.limitExceeded) {
        toast.info(upsertResult.message || `${upsertResult.added} added, ${upsertResult.skipped} skipped (limit reached).`, {
          id: 'folder-import-status', duration: 6000,
        });
      } else if (summary.allDuplicates) {
        toast.info(`All ${summary.duplicateCount} selected file${summary.duplicateCount > 1 ? 's are' : ' is'} already in your library.`, {
          id: 'folder-import-status', duration: 4500,
        });
      } else if (summary.mixedAddedAndDuplicates) {
        toast.success(`${upsertResult.added} added, ${summary.duplicateCount} already in library.`, {
          id: 'folder-import-status', duration: 4500,
        });
      } else if (selection.folderIds.length > 0) {
        toast.success(`Import complete: ${upsertResult.added} items added.`, { id: 'folder-import-status', duration: 4000 });
      }

      // MERGE with existing library items instead of replacing to prevent data loss
      const mergedItems: MediaItem[] = (() => {
        const map = new Map(libraryItems.map(i => [i.id, i]));
        for (const item of items) {
          map.set(item.id, item);
        }
        return Array.from(map.values());
      })();
      setLibraryItems(mergedItems);
      setShowMigrationBanner(false);
      reshuffle(mergedItems, localItems, settings.filter);
      if (userId) {
        const existingMetadata = await storageService.getMediaMetadata(userId);
        const localMetadata = existingMetadata.filter((meta) => meta.source === 'local');
        const driveMetadata = mergedItems
          .filter((item) => item.source === 'drive')
          .map((item) => ({
            id: item.id,
            userId,
            driveFileId: item.driveFile?.id || item.id,
            name: item.name,
            mimeType: item.mimeType,
            source: 'drive' as const,
            indexedAt: new Date().toISOString(),
            thumbnailUrl: item.driveFile?.thumbnailLink,
          }));
        await storageService.saveMediaMetadata([...localMetadata, ...driveMetadata]);
      }

      // Refresh plan usage
      if (authSession) await refreshPlan(authSession);
    } catch (err: any) {
      addSentryBreadcrumb('library_upsert_fail', { error: err?.message || 'unknown' });
      captureSentryException(err, { stage: 'import_from_picker' });
      toast.error(err?.message || 'Failed to import from Drive Picker.', { id: 'folder-import-status', duration: 6000 });
      setError(err?.message || 'Failed to import from Drive Picker.');
    }
  };

  const handleBulkImport = async (inputText?: string) => {
    const text = (inputText ?? '').trim();
    if (!text) { setShowPasteLinksModal(true); return; }
    const parsed = parseDriveLinksAndIds(text);
    const intent = resolveBulkImportIntent(parsed);
    if (intent.type === 'invalid') {
      toast.info('No valid Drive file or folder links found.', { duration: 4000 });
      return;
    }
    if (!intent.presetFileIds && intent.detectedFolderLinks) {
      toast.info('Folder links detected. Continue in Picker and select the folder to import media.', { duration: 5000 });
      await importFromPicker();
      return;
    }
    await importFromPicker(intent.presetFileIds);
  };

  const handleSearch = async (query: string) => {
    if (!userId) return;
    setIsSearching(true);

    try {
      const cachedSearch = await searchCacheService.getCachedSearch(userId, query);
      const allItems = [...libraryItems, ...localItems];

      if (cachedSearch) {
        setSearchTags(cachedSearch.tags);
        setActiveSearchQuery(query);
        const cachedItems = allItems.filter((item) => cachedSearch.resultIds.includes(item.id));
        setSearchResults(cachedItems);
        setIsSearchMode(true);
        setShowSearch(false);
        reshuffle(cachedItems.filter(i => i.source === 'drive'), cachedItems.filter(i => i.source === 'local'), settings.filter);
        await searchCacheService.incrementCacheHit(userId, 'search');
        return;
      }

      // AI gating: if AI search tags disabled, use keyword fallback
      if (!aiEnabled) {
        // Keyword-only search (no Gemini)
        const keywords = query.toLowerCase().split(/\s+/).filter(Boolean);
        setSearchTags(keywords);
        setActiveSearchQuery(query);
        const results = allItems.filter((item) => {
          const name = item.name.toLowerCase();
          return keywords.some((kw) => name.includes(kw));
        });
        setSearchResults(results);
        setIsSearchMode(true);
        setShowSearch(false);
        reshuffle(results.filter(i => i.source === 'drive'), results.filter(i => i.source === 'local'), settings.filter);

        // Show gating message
        toast.info('AI search is a Pro feature. Using keyword search.', { duration: 4000 });
        return;
      }

      const canSearch = await usageService.canPerformAction(userId, 'ai_search');
      if (!canSearch.allowed) {
        setUpgradeReason(canSearch.reason || 'You have reached your AI search limit.');
        setTargetUpgradeTier('pro');
        setShowUpgradePrompt(true);
        setIsSearching(false);
        return;
      }

      const cachedTags = await searchCacheService.getCachedTags(query);
      let tags = cachedTags || await generateSearchTags(query, authSession, true);
      if (!cachedTags) await usageService.trackUsage(userId, 'ai_search');

      setSearchTags(tags);
      setActiveSearchQuery(query);
      const lowered = tags.map((t) => t.toLowerCase());
      const results = allItems.filter((item) => {
        const name = item.name.toLowerCase();
        return lowered.some((tag) => name.includes(tag));
      });

      await searchCacheService.saveCachedSearch(userId, query, tags, results.map((item) => item.id));
      setSearchResults(results);
      setIsSearchMode(true);
      setShowSearch(false);
      reshuffle(results.filter(i => i.source === 'drive'), results.filter(i => i.source === 'local'), settings.filter);
    } catch (err: any) {
      alert('Search failed: ' + err.message);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setIsSearchMode(false);
    setSearchResults([]);
    setActiveSearchQuery('');
    reshuffle(libraryItems, localItems, settings.filter);
  };

  const handleLocalFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !userId) return;
    const files: File[] = Array.from(event.target.files);
    const storesReady = await storageService.assertStoresExist([STORES.LOCAL_FILES, STORES.METADATA]);
    if (!storesReady) {
      toast.error('Local storage is still upgrading. Please wait a few seconds and try again.', { duration: 5000 });
      return;
    }
    const metadataMap = new Map(
      (await storageService.getMediaMetadata(userId)).map((meta) => [meta.id, meta])
    );
    const newItems: MediaItem[] = [];
    for (const file of files) {
      try {
        const hash = await storageService.calculateFileHash(file);
        const existingFile = await storageService.getLocalFile(hash);
        if (existingFile) continue;
        await storageService.saveLocalFile(hash, file, userId);
        const item: MediaItem = { id: `local-${hash.substring(0, 16)}`, name: file.name, mimeType: file.type, source: 'local', file };
        newItems.push(item);
        metadataMap.set(item.id, {
          id: item.id, userId, localFileHash: hash, name: file.name, mimeType: file.type, fileSize: file.size,
          source: 'local' as const, indexedAt: new Date().toISOString(),
        });
      } catch (err) {
        console.error('Error processing local file:', err);
      }
    }
    if (newItems.length > 0) {
      await storageService.saveMediaMetadata(Array.from(metadataMap.values()));
      const updated = [...localItems, ...newItems];
      setLocalItems(updated);
      const syncState = await storageService.getSyncState(userId);
      if (syncState) {
        await storageService.saveSyncState({
          ...syncState,
          lastLocalSyncTime: new Date().toISOString(),
          totalLocalFilesIndexed: (syncState.totalLocalFilesIndexed || 0) + newItems.length,
        });
      }
      if (!isSearchMode) reshuffle(libraryItems, updated, settings.filter);
    }
  };

  // ── Playback Control ──
  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArr = [...array];
    for (let i = newArr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
    }
    return newArr;
  };

  const reshuffle = (dItems: MediaItem[], lItems: MediaItem[], filter: MediaType) => {
    const all = [...dItems, ...lItems];
    let filtered = all;
    if (filter === MediaType.PHOTO) filtered = all.filter(f => getMediaType(f) === 'photo');
    else if (filter === MediaType.VIDEO) filtered = all.filter(f => getMediaType(f) === 'video');
    if (filtered.length === 0) { setShuffledQueue([]); return; }
    setShuffledQueue(shuffleArray(filtered));
    setCurrentIndex(0);
  };

  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % shuffledQueue.length);
  }, [shuffledQueue.length]);

  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + shuffledQueue.length) % shuffledQueue.length);
  };

  const startSlideShow = useCallback(() => {
    if (slideshowTimerRef.current) { window.clearTimeout(slideshowTimerRef.current); slideshowTimerRef.current = null; }
    if (!isMediaLoaded) return;
    slideshowTimerRef.current = window.setTimeout(() => {
      setCurrentIndex((prev) => {
        const queue = shuffledQueueRef.current;
        if (queue.length === 0) return prev;
        return (prev + 1) % queue.length;
      });
    }, settings.interval * 1000);
  }, [settings.interval, isMediaLoaded]);

  const stopSlideShow = () => {
    if (slideshowTimerRef.current) { window.clearTimeout(slideshowTimerRef.current); slideshowTimerRef.current = null; }
  };

  useEffect(() => {
    const currentItem = shuffledQueue[currentIndex];
    const isVideo = currentItem ? getMediaType(currentItem) === 'video' : false;
    if (!settings.isPlaying || isVideo || !isMediaLoaded) { stopSlideShow(); return; }
    startSlideShow();
    return () => stopSlideShow();
  }, [settings.isPlaying, currentIndex, startSlideShow, shuffledQueue, isMediaLoaded]);

  // ── Render ──
  if (!isAuthenticated) {
    if (showWelcomeScreen) return <PlayerRedesign />;
    return <HomepageDesign3 onLogin={handleGetStarted} />;
  }

  // When authenticated, show the full PlayerRedesign app
  return <PlayerRedesign />;

  if (shuffledQueue.length === 0 && !loading && !isSearchMode) {
    return (
      <div className="h-[100dvh] w-screen flex flex-col items-center justify-center bg-black text-white p-4 text-center relative z-50">
        <input type="file" ref={fileInputRef} onChange={handleLocalFileSelect} className="hidden" multiple accept="image/*,video/*" />
        <ImagePlus className="w-16 h-16 text-blue-500 mb-4" />
        <h2 className="text-2xl font-bold mb-2">No Media Found</h2>
        {error ? (
          <div className="mb-6">
            <p className="text-red-400 mb-2 font-semibold">Error: {error}</p>
            <p className="text-gray-400 text-sm">Try signing out and signing in again, or add local files.</p>
          </div>
        ) : (
          <p className="text-gray-400 mb-4">Your filter or Drive is empty.</p>
        )}
        {/* Library usage bar */}
        <div className="mb-4">
          <LibraryUsageBar
            used={driveItemsUsed}
            limit={driveItemsLimit}
            tier={currentTier}
            aiEnabled={aiEnabled}
            collectionsEnabled={collectionsEnabled}
            onUpgradeClick={() => setShowSubscriptionModal(true)}
          />
        </div>
        <div className="flex gap-4 pointer-events-auto z-50 flex-wrap justify-center">
          <button onClick={() => fileInputRef.current?.click()} className="px-6 py-3 bg-blue-600 rounded-full flex items-center gap-2 pointer-events-auto cursor-pointer hover:bg-blue-700 transition-colors"><Plus /> Add Local</button>
          <button onClick={() => importFromPicker()} className="px-6 py-3 bg-indigo-600 rounded-full pointer-events-auto cursor-pointer hover:bg-indigo-700 transition-colors">Add from Drive</button>
          <button onClick={() => setShowPasteLinksModal(true)} className="px-6 py-3 bg-purple-600 rounded-full pointer-events-auto cursor-pointer hover:bg-purple-700 transition-colors">Paste Links</button>
          <button onClick={() => setShowSettings(true)} className="px-6 py-3 bg-gray-800 rounded-full pointer-events-auto cursor-pointer hover:bg-gray-700 transition-colors">Settings</button>
          {error && (
            <button onClick={() => setError(null)} className="px-6 py-3 bg-yellow-600 rounded-full pointer-events-auto cursor-pointer hover:bg-yellow-700 transition-colors">Retry</button>
          )}
          <button onClick={handleLogout} className="px-6 py-3 bg-red-900/50 rounded-full pointer-events-auto cursor-pointer hover:bg-red-800/50 transition-colors">Sign Out</button>
        </div>
        {showSettings && <SettingsModal settings={settings} onClose={() => setShowSettings(false)} onUpdate={s => setSettings(prev => ({ ...prev, ...s }))} />}
      </div>
    );
  }

  const currentItem = shuffledQueue[currentIndex];

  return (
    <div className={`relative h-[100dvh] w-screen overflow-hidden bg-black select-none`}>
      <input type="file" ref={fileInputRef} onChange={handleLocalFileSelect} className="hidden" multiple accept="image/*,video/*" />

      {showMigrationBanner && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-50 bg-amber-700/90 text-white px-3 py-2 rounded-md text-xs">
          Refract now requires explicit Drive file selection. Use "Add from Drive" to rebuild your library.
        </div>
      )}

      {/* Header */}
      <div className={`absolute top-0 left-0 right-0 z-50 p-4 pt-safe flex justify-between items-start bg-gradient-to-b from-black/70 to-transparent transition-opacity duration-300 ${controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className="flex flex-col max-w-[60%]">
          <h1 className="text-white font-bold text-lg drop-shadow-md truncate">{currentItem?.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${currentItem?.source === 'local' ? 'bg-green-500/50' : 'bg-blue-500/50'}`}>
              {currentItem?.source === 'local' ? 'LOCAL' : 'DRIVE'}
            </span>
            <p className="text-xs text-gray-300">{currentIndex + 1} / {shuffledQueue.length}</p>
          </div>
          {/* Library usage bar */}
          <div className="mt-1.5">
            <LibraryUsageBar
              used={driveItemsUsed}
              limit={driveItemsLimit}
              tier={currentTier}
              aiEnabled={aiEnabled}
              collectionsEnabled={collectionsEnabled}
              onUpgradeClick={() => setShowSubscriptionModal(true)}
            />
          </div>
          {isSearchMode && (
            <div className="mt-1 bg-purple-600/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-md inline-flex items-center gap-1 w-fit">
              <Search size={10} /> <span>"{activeSearchQuery}"</span>
              {!aiEnabled && <span className="text-yellow-300 text-[9px]">(keyword only)</span>}
              <button onClick={clearSearch}><XCircle size={12} /></button>
            </div>
          )}
        </div>

        <div className="flex gap-2 pointer-events-auto">
          <button onClick={() => setShowSearch(true)} className="p-2 bg-white/10 rounded-full hover:bg-white/20 text-white"><Search size={20} /></button>
          <button onClick={() => fileInputRef.current?.click()} className="p-2 bg-white/10 rounded-full hover:bg-white/20 text-white"><Plus size={20} /></button>
          <button onClick={() => importFromPicker()} className="p-2 bg-white/10 rounded-full hover:bg-white/20 text-white text-xs">Drive</button>
          <button onClick={() => setShowPasteLinksModal(true)} className="p-2 bg-white/10 rounded-full hover:bg-white/20 text-white text-xs">Links</button>
          <button onClick={() => {
            if (isSearchMode) {
              const d = searchResults.filter(i => i.source === 'drive');
              const l = searchResults.filter(i => i.source === 'local');
              reshuffle(d, l, settings.filter);
            } else {
              reshuffle(libraryItems, localItems, settings.filter);
            }
          }} className="p-2 bg-white/10 rounded-full hover:bg-white/20 text-white"><Shuffle size={20} /></button>
          {userId && (
            <>
              <button onClick={() => setShowApiKeyModal(true)} className="p-2 bg-purple-500/20 rounded-full hover:bg-purple-500/40 text-white" title="API Keys">
                <Key size={20} />
              </button>
              <button onClick={() => setShowSubscriptionModal(true)} className="p-2 bg-yellow-500/20 rounded-full hover:bg-yellow-500/40 text-white" title="Plan & Usage">
                <Crown size={20} />
              </button>
            </>
          )}
          <button onClick={handleLogout} className="p-2 bg-red-500/20 rounded-full hover:bg-red-500/40 text-white"><LogOut size={20} /></button>
        </div>
      </div>

      {/* Player */}
      <EtherealPlayer
        item={currentItem}
        accessToken={accessToken}
        onVideoEnded={() => { if (settings.isPlaying) nextSlide(); }}
        isActive={true}
        onToggleUI={() => setControlsVisible(prev => !prev)}
        onMediaLoaded={() => setIsMediaLoaded(true)}
      />

      {/* Controls */}
      <div className={`transition-opacity duration-300 ${controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <Controls
          isPlaying={settings.isPlaying}
          onPlayPause={() => setSettings(prev => ({ ...prev, isPlaying: !prev.isPlaying }))}
          onNext={nextSlide}
          onPrev={prevSlide}
          onOpenSettings={() => setShowSettings(true)}
          currentItem={currentItem}
          session={authSession}
        />
      </div>

      {showSettings && <SettingsModal settings={settings} onClose={() => setShowSettings(false)} onUpdate={s => setSettings(prev => ({ ...prev, ...s }))} />}
      <SearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} onSearch={handleSearch} isProcessing={isSearching} />
      <PasteLinksModal
        isOpen={showPasteLinksModal}
        onClose={() => setShowPasteLinksModal(false)}
        onSubmit={async (value) => {
          setShowPasteLinksModal(false);
          await handleBulkImport(value);
        }}
      />

      {/* Subscription Management */}
      {userId && (
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          userId={userId}
          session={authSession}
          onPlanChange={(plan) => {
            setPlanData(plan);
            setSubscriptionTier(plan.tier);
          }}
        />
      )}

      {/* Upgrade Prompt */}
      {userId && (
        <UpgradePrompt
          isOpen={showUpgradePrompt}
          onClose={() => setShowUpgradePrompt(false)}
          reason={upgradeReason}
          currentTier={subscriptionTier}
          targetTier={targetUpgradeTier}
          userId={userId}
        />
      )}

      {/* API Key Management */}
      {userId && (
        <ApiKeyModal
          isOpen={showApiKeyModal}
          onClose={() => setShowApiKeyModal(false)}
          userId={userId}
        />
      )}

      {/* Checkout Success */}
      {userId && checkoutSessionId && (
        <CheckoutSuccess
          sessionId={checkoutSessionId}
          userId={userId}
          onComplete={async () => {
            setCheckoutSessionId(null);
            if (authSession) await refreshPlan(authSession);
          }}
        />
      )}

      {/* Checkout Cancel */}
      {showCheckoutCancel && (
        <CheckoutCancel onReturn={() => setShowCheckoutCancel(false)} />
      )}

      <Toaster position="top-center" richColors />
    </div>
  );
};

export default App;
